import {
  Activity,
  BadgeCheck,
  BarChart3,
  Briefcase,
  Building2,
  CalendarDays,
  Clock,
  GraduationCap,
  HeartHandshake,
  Landmark,
  Link2,
  Network,
  PieChart,
  RefreshCw,
  School,
  Store,
  Tags,
  TrendingUp,
  UserRound,
  Users,
  Zap,
} from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, getAdminToken, setAdminToken } from '../../api/client'
import { AdminHeader } from './adminAccess'
import { AdminHero, HeroBadge } from './AdminHero'
import { adminApi } from '../../api/endpoints'
import { roleLabel } from '../../auth/roleOptions'
import { ErrorMessage } from '../../components/ErrorMessage'
import { NEED_CATEGORY_LABELS } from '../../utils/taxonomy'
import type {
  AdminAnalyticsResponse,
  AdminCategoryAnalytics,
  AdminCategoryCount,
  AdminDailyAnalytics,
  AdminRoleCount,
  EntityKind,
  NeedCategory,
} from '../../types/api'
import { DailyBarChart, type DailySeries } from './DailyBarChart'

/** How often the numbers refresh themselves while the tab is open. */
const REFRESH_MS = 60_000

/** Each role's icon and colour, the same on its card and in the mix chart. */
const ROLE_STYLE: Partial<Record<EntityKind, { icon: ReactNode; color: string; tint: string }>> = {
  STUDENT: { icon: <GraduationCap size={18} />, color: '#2a78d6', tint: 'from-sky-500 to-blue-600' },
  TUTOR: { icon: <UserRound size={18} />, color: '#eb6834', tint: 'from-orange-500 to-rose-500' },
  CENTRE: { icon: <Store size={18} />, color: '#8b5cf6', tint: 'from-violet-500 to-purple-600' },
  COUNSELLOR: { icon: <HeartHandshake size={18} />, color: '#ec4899', tint: 'from-pink-500 to-fuchsia-500' },
  GUARDIAN: { icon: <Users size={18} />, color: '#e87ba4', tint: 'from-pink-400 to-rose-400' },
  SCHOOL: { icon: <School size={18} />, color: '#1baf7a', tint: 'from-emerald-500 to-teal-500' },
  COLLEGE: { icon: <Landmark size={18} />, color: '#eda100', tint: 'from-amber-400 to-orange-500' },
  COMPANY: { icon: <Briefcase size={18} />, color: '#0ea5e9', tint: 'from-cyan-500 to-sky-600' },
}
const FALLBACK_STYLE = { icon: <Building2 size={18} />, color: '#64748b', tint: 'from-slate-500 to-slate-600' }
const styleOf = (kind: EntityKind) => ROLE_STYLE[kind] ?? FALLBACK_STYLE
const nameOf = (kind: EntityKind) => (kind === 'STUDENT' ? 'Seeker' : roleLabel(kind))
const pct = (part: number, total: number) => (total === 0 ? 0 : Math.round((part / total) * 100))

/**
 * The ops Analysis tab: how many students, tutors, guardians, schools, colleges and corporates
 * there are, and how many of each are using the app right now.
 *
 * <p>"Online now" is a role seen in the last few minutes, not a live session count - sign-in is a
 * stateless token, so there is no session to count. See AdminAnalyticsController.
 */
export function AdminAnalyticsPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<AdminAnalyticsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const load = useCallback(() => {
    setLoading(true)
    adminApi
      .analytics()
      .then((res) => {
        setData(res)
        setError(null)
      })
      .catch((err) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          signOut()
          return
        }
        setError(apiErrorMessage(err, 'Could not load the dashboard'))
      })
      .finally(() => setLoading(false))
  }, [signOut])

  useEffect(() => {
    if (!getAdminToken()) return
    load()
    const timer = window.setInterval(load, REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [load])

  if (!getAdminToken()) return <Navigate to="/admin/login" replace />

  const verifiable = data ? data.verified + data.unverified : 0

  return (
    <div className="admin-console min-h-screen bg-paper lg:pl-64">
      <AdminHeader current="analytics" onSignOut={signOut} width="max-w-6xl" />

      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
        <AdminHero
          icon={<BarChart3 size={26} />}
          eyebrow="Dashboard"
          title="Analysis"
          blurb="Accounts by role, and how many are using YellowDots right now."
        >
          <HeroBadge>
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> Live - every minute
          </HeroBadge>
          {data && (
            <HeroBadge>
              <Clock size={13} className="text-amber-600" /> {new Date(data.generatedAt).toLocaleTimeString()}
            </HeroBadge>
          )}
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-full bg-sun-400 px-4 py-1.5 text-xs font-bold text-deep-900 shadow-md transition hover:bg-sun-300 disabled:opacity-70"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </AdminHero>

        <ErrorMessage message={error} />

        {/* KPI tiles */}
        <section className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <Kpi
            icon={<Users size={18} />}
            tone="from-sky-500 to-indigo-500"
            label="People"
            value={data?.people}
            hint="Each person once, however many roles"
          />
          <Kpi
            icon={<Activity size={18} />}
            tone="from-emerald-500 to-teal-500"
            label="Online now"
            value={data?.onlineNow}
            hint={data ? `Active in the last ${data.onlineWindowMinutes} minutes` : undefined}
            live
          />
          <Kpi
            icon={<Zap size={18} />}
            tone="from-orange-500 to-rose-500"
            label="Active today"
            value={data?.activeToday}
            hint="Active in the last 24 hours"
          />
          <Kpi
            icon={<BadgeCheck size={18} />}
            tone="from-dot-500 to-sun-400"
            label="Verified providers"
            value={data?.verified}
            hint={data ? `${pct(data.verified, verifiable)}% of ${verifiable.toLocaleString()} providers` : undefined}
          />
          <Kpi
            icon={<Link2 size={18} />}
            tone="from-violet-500 to-fuchsia-500"
            label="Connections"
            value={data?.connections}
            hint={data ? `Accepted · ${data.pendingConnections.toLocaleString()} pending` : undefined}
          />
        </section>

        {data && (
          <>
            {/* Infographics */}
            <section className="grid gap-4 lg:grid-cols-5">
              <Panel
                className="lg:col-span-3"
                icon={<PieChart size={16} className="text-indigo-600" />}
                title="Role mix"
                subtitle="Every role account, by kind"
              >
                <RoleMix roles={data.roles} total={data.totalRoles} />
              </Panel>
              <Panel
                className="lg:col-span-2"
                icon={<BadgeCheck size={16} className="text-emerald-600" />}
                title="Provider verification"
                subtitle="Roles that go through verification"
              >
                <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-around lg:flex-col xl:flex-row">
                  <Ring value={pct(data.verified, verifiable)} color="#10b981" label="verified" />
                  <div className="w-full max-w-[12rem] space-y-2">
                    <LegendRow color="bg-emerald-500" label="Verified" value={data.verified} />
                    <LegendRow color="bg-amber-400" label="Unverified" value={data.unverified} />
                    <LegendRow color="bg-ink-300" label="Total" value={verifiable} bold />
                  </div>
                </div>
              </Panel>
            </section>

            {/* Role cards */}
            <section>
              <div className="mb-3 flex items-center gap-2">
                <Network size={16} className="text-ink-500" />
                <h2 className="text-lg font-extrabold text-ink-900">By role</h2>
                <span className="text-xs text-ink-500">- {data.totalRoles.toLocaleString()} role accounts</span>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {data.roles.map((r) => (
                  <RoleCard key={r.entityKind} role={r} total={data.totalRoles} />
                ))}
              </div>
              <p className="mt-3 text-xs text-ink-400">
                Counted per role: someone who is a seeker and a tutor appears in both, and once under
                People. "Online now" counts from when this tab was added - earlier visits were not
                recorded. Refreshes every minute.
              </p>
            </section>
          </>
        )}

        <SignupCharts onUnauthorized={signOut} />
        <CategoryCharts onUnauthorized={signOut} />
      </main>
    </div>
  )
}

/** Categorical slots 1-5 of the validated reference palette, in fixed order - see DailyBarChart. */
const SLOT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4']

const PROVIDER_SERIES: { key: string; label: string }[] = [
  { key: 'TUTOR', label: 'Tutor' },
  { key: 'CENTRE', label: 'Coaching centre' },
  { key: 'SCHOOL', label: 'School' },
  { key: 'COLLEGE', label: 'College' },
  { key: 'COMPANY', label: 'Corporate' },
]

const RANGES = [30, 90, 180, 365]

/** One colour per category, fixed - never reassigned by rank - so a category reads the same colour everywhere. */
const CATEGORY_COLORS: Record<NeedCategory, string> = {
  ACADEMICS: '#2a78d6',
  COMPETITIVE_EXAMS: '#eb6834',
  MUSIC: '#1baf7a',
  SPORTS_FITNESS: '#eda100',
  ARTS_CREATIVITY: '#e87ba4',
  SKILLS_TECHNOLOGY: '#8b5cf6',
  AI_FLUENCY: '#0ea5e9',
  LANGUAGES: '#ec4899',
  SCHOOL_ADMISSION: '#64748b',
  HIGHER_EDUCATION: '#22c55e',
  INTERNSHIPS: '#f43f5e',
  ENTREPRENEURSHIP: '#a855f7',
}

/** Turns a per-category daily map into the chart's hover-breakdown series, one per category. */
function categorySeriesFrom(byCategory: Record<string, number[]>): DailySeries[] {
  return Object.entries(byCategory).map(([key, values]) => ({
    key,
    label: NEED_CATEGORY_LABELS[key as NeedCategory],
    color: CATEGORY_COLORS[key as NeedCategory],
    values,
  }))
}

/**
 * Registrations per day: seekers, providers by kind, and aggregators. One range control above all
 * three, so the charts always cover the same days.
 */
function SignupCharts({ onUnauthorized }: { onUnauthorized: () => void }) {
  const [days, setDays] = useState(30)
  const [daily, setDaily] = useState<AdminDailyAnalytics | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    adminApi
      .analyticsDaily(days)
      .then((res) => {
        if (!cancelled) {
          setDaily(res)
          setError(null)
        }
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          onUnauthorized()
          return
        }
        setError(apiErrorMessage(err, 'Could not load the daily sign-ups'))
      })
    return () => {
      cancelled = true
    }
  }, [days, onUnauthorized])

  const sum = (xs: number[] | null | undefined) => (xs ?? []).reduce((a, b) => a + b, 0)
  const seekerTotal = daily ? sum(daily.students) : 0
  const providerTotal = daily ? Object.values(daily.providers).reduce((a, xs) => a + sum(xs), 0) : 0
  const aggregatorTotal = daily ? sum(daily.organisations) + sum(daily.coordinators) : 0

  return (
    <div className="space-y-5 pt-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-fuchsia-500 to-indigo-500 text-white shadow-md">
            <TrendingUp size={18} />
          </span>
          <div>
            <h2 className="text-xl font-extrabold text-ink-900">Sign-ups per day</h2>
            <p className="text-sm text-ink-500">New registrations each day (India time), per role.</p>
          </div>
        </div>
        <div className="inline-flex items-center gap-1 rounded-full bg-white p-1 shadow-card" role="group" aria-label="Date range">
          <CalendarDays size={14} className="ml-2 text-ink-400" />
          {RANGES.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              aria-pressed={days === d}
              className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${
                days === d ? 'bg-deep-900 text-white shadow' : 'text-ink-600 hover:bg-ink-100'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      <ErrorMessage message={error} />

      {daily && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <MiniTotal icon={<GraduationCap size={16} />} tone="from-sky-500 to-blue-600" label="New seekers" value={seekerTotal} days={days} />
            <MiniTotal icon={<Store size={16} />} tone="from-orange-500 to-rose-500" label="New providers" value={providerTotal} days={days} />
            {daily.organisations && daily.coordinators && (
              <MiniTotal icon={<Network size={16} />} tone="from-violet-500 to-purple-600" label="New aggregators" value={aggregatorTotal} days={days} />
            )}
          </div>
          <DailyBarChart
            title="Seekers"
            subtitle="Seeker registrations per day"
            icon={<GraduationCap size={18} />}
            accent="from-sky-500 to-blue-600"
            days={daily.days}
            series={[{ key: 'STUDENT', label: 'Seekers', color: SLOT[0], values: daily.students }]}
            categoryBreakdown={categorySeriesFrom(daily.studentCategories)}
          />
          <DailyBarChart
            title="Providers"
            subtitle="Provider registrations per day, by kind"
            icon={<Store size={18} />}
            accent="from-orange-500 to-rose-500"
            days={daily.days}
            series={PROVIDER_SERIES.map((p, i) => ({
              key: p.key,
              label: p.label,
              color: SLOT[i],
              values: daily.providers[p.key] ?? [],
            }))}
            categoryBreakdown={categorySeriesFrom(daily.providerCategories)}
          />
          {/* Super admin only: the server sends no aggregator series to any other role. */}
          {daily.organisations && daily.coordinators && (
            <DailyBarChart
              title="Aggregators"
              subtitle="Organisations and coordinators joining the aggregator portal per day"
              icon={<Network size={18} />}
              accent="from-violet-500 to-purple-600"
              days={daily.days}
              series={[
                { key: 'ORG', label: 'Organisations', color: SLOT[0], values: daily.organisations },
                { key: 'COORDINATOR', label: 'Coordinators', color: SLOT[1], values: daily.coordinators },
              ]}
            />
          )}
        </>
      )}
    </div>
  )
}

/**
 * What seekers are looking for vs. what providers offer, by taxonomy category (see Image #1's
 * "What are you looking for?" chips) - a consolidated count per category, with the exact number
 * and share on hover. Loads on its own, same pattern as SignupCharts.
 */
function CategoryCharts({ onUnauthorized }: { onUnauthorized: () => void }) {
  const [data, setData] = useState<AdminCategoryAnalytics | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    adminApi
      .analyticsCategories()
      .then((res) => {
        if (!cancelled) {
          setData(res)
          setError(null)
        }
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          onUnauthorized()
          return
        }
        setError(apiErrorMessage(err, 'Could not load the category breakdown'))
      })
    return () => {
      cancelled = true
    }
  }, [onUnauthorized])

  return (
    <div className="space-y-5 pt-4">
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-md">
          <Tags size={18} />
        </span>
        <div>
          <h2 className="text-xl font-extrabold text-ink-900">By category</h2>
          <p className="text-sm text-ink-500">
            What seekers are looking for, and what providers offer - hover a bar for the exact count and share.
          </p>
        </div>
      </div>

      <ErrorMessage message={error} />

      {data && (
        <section className="grid gap-4 lg:grid-cols-2">
          <Panel icon={<GraduationCap size={16} className="text-sky-600" />} title="Seekers" subtitle="Needs posted, by category">
            <CategoryBars rows={data.seekers} />
          </Panel>
          <Panel icon={<Store size={16} className="text-orange-600" />} title="Providers" subtitle="Services offered, by category">
            <CategoryBars rows={data.providers} />
          </Panel>
        </section>
      )}
    </div>
  )
}

/** Sorted bars, tallest first; each bar's hover title carries the exact count and its share. */
function CategoryBars({ rows }: { rows: AdminCategoryCount[] }) {
  const sorted = [...rows].sort((a, b) => b.total - a.total)
  const total = sorted.reduce((s, r) => s + r.total, 0)
  const max = Math.max(1, ...sorted.map((r) => r.total))
  return (
    <ul className="space-y-2.5">
      {sorted.map((r) => {
        const color = CATEGORY_COLORS[r.category]
        const label = NEED_CATEGORY_LABELS[r.category]
        return (
          <li
            key={r.category}
            className="grid grid-cols-[8.5rem_1fr_auto] items-center gap-3 text-sm"
            title={`${label}: ${r.total.toLocaleString()} (${pct(r.total, total)}%)`}
          >
            <span className="truncate font-semibold text-ink-800">{label}</span>
            <span className="h-2.5 overflow-hidden rounded-full bg-ink-100">
              <span
                className="block h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.max((r.total / max) * 100, r.total > 0 ? 2 : 0)}%`, background: color }}
              />
            </span>
            <span className="w-20 text-right tabular-nums">
              <span className="font-bold text-ink-900">{r.total.toLocaleString()}</span>{' '}
              <span className="text-xs text-ink-400">{pct(r.total, total)}%</span>
            </span>
          </li>
        )
      })}
    </ul>
  )
}

function Kpi({
  icon,
  tone,
  label,
  value,
  hint,
  live,
}: {
  icon: ReactNode
  tone: string
  label: string
  value: number | undefined
  hint?: string
  live?: boolean
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-white p-4 shadow-card">
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${tone}`} aria-hidden />
      <div className={`pointer-events-none absolute -bottom-8 -right-8 h-24 w-24 rounded-full bg-gradient-to-br ${tone} opacity-10`} aria-hidden />
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-ink-500">
          {live && <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" aria-hidden="true" />}
          {label}
        </span>
        <span className={`grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br ${tone} text-white shadow-md`}>{icon}</span>
      </div>
      <div className="mt-2 text-3xl font-extrabold tabular-nums tracking-tight text-ink-900">
        {value === undefined ? <span className="inline-block h-8 w-20 animate-pulse rounded bg-ink-100" /> : value.toLocaleString()}
      </div>
      {hint && <div className="mt-0.5 text-xs text-ink-500">{hint}</div>}
    </div>
  )
}

function Panel({
  icon,
  title,
  subtitle,
  className = '',
  children,
}: {
  icon: ReactNode
  title: string
  subtitle: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={`rounded-2xl bg-white p-5 shadow-card ${className}`}>
      <h2 className="flex items-center gap-2 text-sm font-bold text-ink-900">
        {icon} {title}
      </h2>
      <p className="text-xs text-ink-500">{subtitle}</p>
      <div className="mt-4">{children}</div>
    </div>
  )
}

/**
 * One bar split by role, then each role's share. Seekers dwarf everyone else, so the bars below
 * scale to the largest role rather than to the total - otherwise every provider row reads as empty.
 */
function RoleMix({ roles, total }: { roles: AdminRoleCount[]; total: number }) {
  const sorted = [...roles].sort((a, b) => b.total - a.total)
  const max = Math.max(1, ...sorted.map((r) => r.total))
  return (
    <div>
      <div className="flex h-4 w-full overflow-hidden rounded-full bg-ink-100">
        {sorted.map((r) =>
          r.total > 0 ? (
            <div
              key={r.entityKind}
              style={{ width: `${(r.total / Math.max(total, 1)) * 100}%`, background: styleOf(r.entityKind).color, minWidth: 4 }}
              title={`${nameOf(r.entityKind)}: ${r.total.toLocaleString()}`}
            />
          ) : null,
        )}
      </div>
      <ul className="mt-4 space-y-2.5">
        {sorted.map((r) => {
          const s = styleOf(r.entityKind)
          return (
            <li key={r.entityKind} className="grid grid-cols-[8.5rem_1fr_auto] items-center gap-3 text-sm">
              <span className="flex min-w-0 items-center gap-2 font-semibold text-ink-800">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-white" style={{ background: s.color }}>
                  {s.icon}
                </span>
                <span className="truncate">{nameOf(r.entityKind)}</span>
              </span>
              <span className="h-2.5 overflow-hidden rounded-full bg-ink-100">
                <span
                  className="block h-full rounded-full transition-all duration-700"
                  style={{ width: `${Math.max((r.total / max) * 100, r.total > 0 ? 2 : 0)}%`, background: s.color }}
                />
              </span>
              <span className="w-24 text-right tabular-nums">
                <span className="font-bold text-ink-900">{r.total.toLocaleString()}</span>{' '}
                <span className="text-xs text-ink-400">{pct(r.total, total)}%</span>
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function RoleCard({ role, total }: { role: AdminRoleCount; total: number }) {
  const s = styleOf(role.entityKind)
  const verifiable = role.verified === null || role.unverified === null ? null : role.verified + role.unverified
  return (
    <div className="group relative overflow-hidden rounded-2xl bg-white shadow-card transition hover:-translate-y-0.5 hover:shadow-float">
      <div className={`flex items-center justify-between bg-gradient-to-r ${s.tint} px-4 py-3 text-white`}>
        <span className="flex items-center gap-2 font-bold">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-white/20 ring-1 ring-white/30">{s.icon}</span>
          {nameOf(role.entityKind)}
        </span>
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-bold">{pct(role.total, total)}%</span>
      </div>
      <div className="p-4">
        <div className="text-3xl font-extrabold tabular-nums tracking-tight text-ink-900">{role.total.toLocaleString()}</div>
        <div className="text-xs text-ink-500">accounts</div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="rounded-xl bg-emerald-50 px-3 py-2">
            <div className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
              <Activity size={12} /> Online now
            </div>
            <div className="text-lg font-extrabold tabular-nums text-emerald-800">{role.onlineNow.toLocaleString()}</div>
          </div>
          <div className="rounded-xl bg-orange-50 px-3 py-2">
            <div className="flex items-center gap-1 text-[11px] font-semibold text-orange-700">
              <Zap size={12} /> Active today
            </div>
            <div className="text-lg font-extrabold tabular-nums text-orange-800">{role.activeToday.toLocaleString()}</div>
          </div>
        </div>

        {verifiable === null ? (
          <div className="mt-3 text-[11px] text-ink-400">Not a verified role</div>
        ) : (
          <div className="mt-3">
            <div className="flex justify-between text-[11px] font-semibold">
              <span className="text-emerald-700">{role.verified!.toLocaleString()} verified</span>
              <span className="text-amber-700">{role.unverified!.toLocaleString()} unverified</span>
            </div>
            <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-amber-200">
              <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${pct(role.verified!, verifiable)}%` }} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** A donut gauge for one percentage, with the number in the middle. */
function Ring({ value, color, label }: { value: number; color: string; label: string }) {
  const r = 42
  const c = 2 * Math.PI * r
  return (
    <div className="relative h-36 w-36 shrink-0">
      <svg viewBox="0 0 100 100" className="h-36 w-36 -rotate-90" role="img" aria-label={`${value}% ${label}`}>
        <circle cx="50" cy="50" r={r} fill="none" stroke="#fde68a" strokeWidth="11" />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="11"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - value / 100)}
          style={{ transition: 'stroke-dashoffset 900ms ease' }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div className="text-3xl font-extrabold text-ink-900">{value}%</div>
          <div className="text-[11px] font-semibold text-ink-500">{label}</div>
        </div>
      </div>
    </div>
  )
}

function LegendRow({ color, label, value, bold }: { color: string; label: string; value: number; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="flex items-center gap-2 text-ink-600">
        <span className={`h-2.5 w-2.5 rounded-full ${color}`} /> {label}
      </span>
      <span className={`tabular-nums ${bold ? 'font-extrabold text-ink-900' : 'font-semibold text-ink-800'}`}>
        {value.toLocaleString()}
      </span>
    </div>
  )
}

function MiniTotal({ icon, tone, label, value, days }: { icon: ReactNode; tone: string; label: string; value: number; days: number }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white p-4 shadow-card">
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br ${tone} text-white shadow-md`}>{icon}</span>
      <div>
        <div className="text-2xl font-extrabold tabular-nums text-ink-900">{value.toLocaleString()}</div>
        <div className="text-xs text-ink-500">
          {label} - last {days} days
        </div>
      </div>
    </div>
  )
}
